import React, { useState, useEffect, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { StyledReactMarkdown } from './Playground';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import 'katex/dist/katex.min.css';
import CodeBlock from '../components/code/CodeBlock';
import TableBlock from '../components/code/TableBlock';
import InlineCitation from '../components/chat/InlineCitation';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, codeLanguageSubset } from '../shared/Constants';
import { Reference, ContentItem, TextContentItem, FileRefContentItem } from '../shared/interfaces';
import maritacaLogoPreta from '../static/images/maritaca_logo_preta.png';
import AttachFileIcon from '@mui/icons-material/AttachFile';

interface SharedMessage {
  role: 'user' | 'assistant';
  content: string | ContentItem[];
  references?: Reference[];
  filenames?: string[];
  files?: { file_id?: string; filename?: string }[];
}

interface SharedData {
  share_id: string;
  title: string;
  messages: SharedMessage[];
  model: string | null;
  access: string;
  created_at: string | null;
}

const getTextContent = (content: string | ContentItem[]): string => {
  if (typeof content === 'string') return content;
  return content
    .filter((item): item is TextContentItem => item.type === 'text')
    .map(item => item.text)
    .join('\n');
};

const preprocessLatex = (text: string | any) => {
  if (!text) return "";
  if (typeof text !== 'string') {
    if (Array.isArray(text)) {
      text = text.filter((item: any) => item.type === 'text').map((item: any) => item.text).join('\n');
    } else {
      text = String(text);
    }
  }
  return text
    .replace(/\\\\\[/g, '$$$$')
    .replace(/\\\\\]/g, '$$$$')
    .replace(/\\\\\(/g, '$$$$')
    .replace(/\\\\\)/g, '$$$$')
    .replace(/\\\[/g, '$$$$')
    .replace(/\\\]/g, '$$$$')
    .replace(/\\\(/g, '$$$$')
    .replace(/\\\)/g, '$$$$');
};

const CodeComponent = memo((props: any) => {
  const { inline, className, children } = props;
  const match = /language-(\w+)/.exec(className || '');
  const lang = match && match[1];

  if (inline) {
    return <code className={className}>{children}</code>;
  }
  return <CodeBlock lang={lang || 'text'} codeChildren={children} />;
});

const StableTableBlock = React.memo(({ markdown }: { markdown: string }) => (
  <TableBlock>
    <StyledReactMarkdown remarkPlugins={[remarkGfm]} components={{
      table: ({ node, ...props }: any) => <table {...props} />,
    }}>
      {markdown}
    </StyledReactMarkdown>
  </TableBlock>
));
StableTableBlock.displayName = 'StableTableBlock';

function SharedConversation() {
  const { shareId } = useParams<{ shareId: string }>();
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [data, setData] = useState<SharedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shareId || authLoading) return;

    const fetchShare = async () => {
      try {
        const headers: Record<string, string> = {};
        if (isAuthenticated) {
          try {
            const token = await getAccessTokenSilently();
            headers['Authorization'] = `Auth ${token}`;
          } catch { /* proceed without token */ }
        }

        const res = await fetch(`${API_BASE}/api/shared/${shareId}`, { headers });
        if (!res.ok) {
          if (res.status === 404) throw new Error('not_found');
          if (res.status === 401) throw new Error('login_required');
          if (res.status === 403) throw new Error('forbidden');
          throw new Error('unknown');
        }
        const json = await res.json();
        setData(json);
      } catch (err: any) {
        setError(err.message);
      }
      setLoading(false);
    };

    fetchShare();
  }, [shareId, isAuthenticated, authLoading, getAccessTokenSilently]);

  const formatAssistantMessage = (message: string, references?: Reference[]) => {
    if (!message) return null;
    const preprocessed = preprocessLatex(message);

    const allAvailableRefs = references || [];

    const seenLinks = new Set<string>();

    const processTextWithCitations = (text: string): React.ReactNode => {
      if (allAvailableRefs.length === 0) return text;
      // Match: markdown links [text](url) | legacy [REFERENCIA_N] | raw URLs
      const citationPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\[(REFERENCIA_\d+(?:\s*,\s*REFERENCIA_\d+)*)\]|(https?:\/\/[^\s<>)]+)/g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;
      let keyIndex = 0;

      while ((match = citationPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }

        if (match[1] && match[2]) {
          // Markdown link: [text](url)
          const linkText = match[1];
          const linkUrl = match[2];
          if (seenLinks.has(linkUrl)) {
            lastIndex = match.index + match[0].length;
            continue;
          }
          seenLinks.add(linkUrl);
          const matchedRef = allAvailableRefs.find(ref => ref.link === linkUrl);
          parts.push(
            <InlineCitation
              key={`cite-${keyIndex++}-link`}
              citationNumber={matchedRef?.index}
              reference={matchedRef}
              linkText={linkText}
              linkUrl={linkUrl}
            />
          );
        } else if (match[3]) {
          // Legacy [REFERENCIA_N] (backward compat with old conversations)
          const individualCitations = match[3].split(/\s*,\s*/);
          individualCitations.forEach((citation) => {
            const numMatch = citation.match(/REFERENCIA_(\d+)/);
            if (numMatch) {
              const citationNumber = parseInt(numMatch[1], 10);
              const reference = allAvailableRefs.find(ref => ref.index === citationNumber);
              parts.push(
                <InlineCitation
                  key={`cite-${keyIndex++}-${citationNumber}`}
                  citationNumber={citationNumber}
                  reference={reference}
                />
              );
            }
          });
        } else if (match[4]) {
          // Raw URL fallback
          const rawUrl = match[4];
          if (seenLinks.has(rawUrl)) {
            lastIndex = match.index + match[0].length;
            continue;
          }
          seenLinks.add(rawUrl);
          const matchedRef = allAvailableRefs.find(ref => ref.link === rawUrl);
          parts.push(
            <InlineCitation
              key={`cite-${keyIndex++}-rawurl`}
              citationNumber={matchedRef?.index}
              reference={matchedRef}
              linkText={matchedRef?.title || rawUrl}
              linkUrl={rawUrl}
            />
          );
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }
      return parts.length > 0 ? parts : text;
    };

    const processChildren = (children: React.ReactNode): React.ReactNode => {
      if (typeof children === 'string') return processTextWithCitations(children);
      if (Array.isArray(children)) {
        return children.map((child, idx) => {
          if (typeof child === 'string') {
            return <React.Fragment key={idx}>{processTextWithCitations(child)}</React.Fragment>;
          }
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { key: idx } as any, processChildren((child.props as any).children));
          }
          return child;
        });
      }
      if (React.isValidElement(children)) {
        return React.cloneElement(children, {}, processChildren((children.props as any).children));
      }
      return children;
    };

    // Split message into segments: tables and text (same as Playground)
    const workingMessage = preprocessed;

    const segments: Array<{type: 'text' | 'table', content: string}> = [
      {type: 'text', content: workingMessage},
    ];

    // Extract tables from text segments
    const tableRegex = /(?:^|\n)((?:\|[^\n]+\|\s*\n)\|[-:\s|]+\|\s*\n(?:\|[^\n]+\|\s*\n?)+)/g;
    const withTables: typeof segments = [];
    for (const seg of segments) {
      if (seg.type !== 'text') {
        withTables.push(seg);
        continue;
      }
      let tLastIndex = 0;
      let tMatch;
      let foundTable = false;
      tableRegex.lastIndex = 0;
      while ((tMatch = tableRegex.exec(seg.content)) !== null) {
        foundTable = true;
        const matchStart = tMatch.index + (tMatch[0].startsWith('\n') ? 1 : 0);
        if (matchStart > tLastIndex) {
          const before = seg.content.slice(tLastIndex, matchStart).trim();
          if (before) withTables.push({type: 'text', content: before});
        }
        withTables.push({type: 'table', content: tMatch[1].trim()});
        tLastIndex = tMatch.index + tMatch[0].length;
      }
      if (foundTable) {
        if (tLastIndex < seg.content.length) {
          const trimmed = seg.content.slice(tLastIndex).trim();
          if (trimmed) withTables.push({type: 'text', content: trimmed});
        }
      } else {
        withTables.push(seg);
      }
    }
    segments.length = 0;
    segments.push(...withTables);

    const components: any = {
      code: CodeComponent,
      table: ({ node, children, ...props }: any) => (
        <TableBlock><table {...props}>{children}</table></TableBlock>
      ),
      p: ({ node, children, ...props }: any) => {
        const hasDisplayMath = node?.children?.some((child: any, index: number, array: any[]) => {
          if (child.type === 'element' && child.properties?.className?.includes('math')) {
            const prevNode = array[index - 1];
            const nextNode = array[index + 1];
            const hasLeadingNewline = !prevNode || (prevNode.type === 'text' && /\n\s*$/.test(prevNode.value));
            const hasTrailingNewline = !nextNode || (nextNode.type === 'text' && /^\s*\n/.test(nextNode.value));
            return hasLeadingNewline && hasTrailingNewline;
          }
          return false;
        });
        return (
          <p className={`whitespace-pre-wrap ${hasDisplayMath ? 'katex-centered' : ''}`} {...props}>
            {processChildren(children)}
          </p>
        );
      },
      li: ({ node, children, ...props }: any) => (
        <li {...props}>{processChildren(children)}</li>
      ),
      td: ({ node, children, ...props }: any) => (
        <td {...props}>{processChildren(children)}</td>
      ),
      th: ({ node, children, ...props }: any) => (
        <th {...props}>{processChildren(children)}</th>
      ),
      a: ({ node, href, children, ...props }: any) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
      ),
      img: ({ node, src, alt, ...props }: any) => (
        <img
          src={src}
          alt={alt || ''}
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
            marginBlock: '0.75rem',
            display: 'block',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          }}
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          {...props}
        />
      ),
    };

    const markdownProps = {
      linkTarget: "_blank" as const,
      remarkPlugins: [
        remarkGfm,
        [remarkMath, { singleDollarTextMath: false }],
      ] as any,
      rehypePlugins: [
        rehypeKatex,
        [rehypeHighlight, { detect: true, ignoreMissing: true, subset: codeLanguageSubset }],
      ] as any,
      components,
    };

    return (
      <StyledKatexContainer>
        {segments.map((seg, i) =>
          seg.type === 'table' ? (
            <StableTableBlock key={`table-${i}-${seg.content.length}`} markdown={seg.content} />
          ) : (
            <StyledReactMarkdown key={`text-${i}`} {...markdownProps}>
              {seg.content}
            </StyledReactMarkdown>
          )
        )}
      </StyledKatexContainer>
    );
  };

  const renderMessageContent = (msg: SharedMessage) => {
    const content = msg.content;
    const refs = msg.references || [];

    if (typeof content === 'string') {
      return (
        <>
          {content ? formatAssistantMessage(content, refs) : null}
        </>
      );
    }

    // Render ContentItem[] in order: text segments and file_ref images
    return (
      <>
        {content.map((item, i) => {
          if (item.type === 'text' && (item as TextContentItem).text) {
            return (
              <React.Fragment key={i}>
                {formatAssistantMessage((item as TextContentItem).text, refs)}
              </React.Fragment>
            );
          }
          if (item.type === 'file_ref') {
            const fileRef = item as FileRefContentItem;
            const blobUrl = `${API_BASE}/api/shared/${shareId}/files/${fileRef.file_id}/blob`;
            const isImage = (fileRef.mime_type || '').startsWith('image/');
            if (isImage) {
              return (
                <SharedFileImage
                  key={i}
                  src={blobUrl}
                  alt={fileRef.title || 'Arquivo gerado'}
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              );
            }
            return (
              <SharedFileLink key={i} href={blobUrl} target="_blank" rel="noopener noreferrer">
                {fileRef.title || 'Arquivo gerado'}
              </SharedFileLink>
            );
          }
          if (item.type === 'input_file') {
            const inputFile = item as any;
            const mime = inputFile.mime_type || 'application/octet-stream';
            const isImage = mime.startsWith('image/');
            if (isImage && inputFile.file_data) {
              const src = inputFile.file_data.startsWith('data:')
                ? inputFile.file_data
                : `data:${mime};base64,${inputFile.file_data}`;
              return (
                <SharedFileImage
                  key={i}
                  src={src}
                  alt={inputFile.filename || 'Arquivo gerado'}
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              );
            }
          }
          return null;
        })}
      </>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Carregando conversa...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    const errorMessages: Record<string, { title: string; desc: string }> = {
      not_found: { title: 'Conversa não encontrada', desc: 'Este link pode ter sido revogado ou não existe.' },
      login_required: { title: 'Login necessário', desc: 'Esta conversa é privada. Faça login para visualizar.' },
      forbidden: { title: 'Acesso negado', desc: 'Você não tem permissão para visualizar esta conversa.' },
      unknown: { title: 'Erro', desc: 'Ocorreu um erro ao carregar a conversa.' },
    };
    const msg = errorMessages[error] || errorMessages.unknown;

    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{msg.title}</ErrorTitle>
          <ErrorDesc>{msg.desc}</ErrorDesc>
          {error === 'login_required' ? (
            <LoginButton onClick={() => loginWithRedirect({ appState: { returnTo: window.location.pathname } })}>
              Fazer login
            </LoginButton>
          ) : (
            <CTAButton to="/">Ir para o Chat Maritaca</CTAButton>
          )}
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!data) return null;

  const formattedDate = data.created_at
    ? new Date(data.created_at).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <PageContainer>
      <Header>
        <LogoLink to="/">
          <Logo src={maritacaLogoPreta} alt="Maritaca AI" />
        </LogoLink>
        <HeaderRight>
          <CTAButtonSmall to="/">Faca sua propria pergunta</CTAButtonSmall>
        </HeaderRight>
      </Header>

      <ContentArea>
        <ConversationHeader>
          <ConversationTitle>{data.title || 'Conversa compartilhada'}</ConversationTitle>
          {formattedDate && <ConversationDate>Compartilhado em {formattedDate}</ConversationDate>}
        </ConversationHeader>

        <MessagesContainer>
          {data.messages.map((msg, index) => {
            if (msg.role === 'user') {
              const textContent = getTextContent(msg.content);
              const fileNames = (msg.filenames && msg.filenames.length > 0)
                ? msg.filenames
                : (msg.files || []).map((f) => f?.filename).filter(Boolean) as string[];
              return (
                <UserMessage key={index}>
                  <UserBubble>
                    {textContent && (
                      <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{textContent}</p>
                    )}
                    {fileNames.length > 0 && (
                      <SharedFileList>
                        {fileNames.map((fn, i) => (
                          <SharedFileChip key={i} title={fn}>
                            <AttachFileIcon style={{ fontSize: 14 }} />
                            <span>{fn}</span>
                          </SharedFileChip>
                        ))}
                      </SharedFileList>
                    )}
                  </UserBubble>
                </UserMessage>
              );
            }

            const textContent = getTextContent(msg.content);
            if (!textContent) return null;

            return (
              <AssistantMessage key={index}>
                <AssistantContent>
                  {renderMessageContent(msg)}
                </AssistantContent>
              </AssistantMessage>
            );
          })}
        </MessagesContainer>
      </ContentArea>

      <Footer>
        <FooterText>
          Gerado com <FooterLink href="https://chat.maritaca.ai" target="_blank" rel="noopener noreferrer">Chat Maritaca</FooterLink> por Maritaca AI
        </FooterText>
        <CTAButton to="/">Faca sua propria pergunta</CTAButton>
      </Footer>
    </PageContainer>
  );
}

export default SharedConversation;

// --- Styled Components ---

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: #1C2724;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
  @media (max-width: 600px) {
    padding: 12px 16px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const Logo = styled.img`
  height: 32px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CTAButtonSmall = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  background: #1C2724;
  color: white;
  border-radius: 24px;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.1s ease;
  &:hover {
    background: #2d3b37;
    transform: translateY(-1px);
  }
  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 6px 14px;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px;
  @media (max-width: 600px) {
    padding: 16px 12px;
  }
`;

const ConversationHeader = styled.div`
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const ConversationTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: #1C2724;
`;

const ConversationDate = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const UserMessage = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const UserBubble = styled.div`
  background: #f3f4f6;
  border-radius: 20px;
  padding: 12px 18px;
  max-width: 80%;
  font-size: 1rem;
  line-height: 1.6;
  color: #1C2724;
`;

const SharedFileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;

  &:first-child {
    margin-top: 0;
  }
`;

const SharedFileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 240px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 5px 10px;
  font-size: 0.82rem;
  color: #374151;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const AssistantMessage = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const AssistantContent = styled.div`
  width: 100%;
  font-size: 1rem;
  line-height: 1.8;
  color: #1C2724;
  overflow-wrap: break-word;

  & blockquote {
    border-left: 4px solid #d1d5db;
    padding-left: 16px;
    color: #6b7280;
    margin: 16px 0;
  }
  & a {
    color: #4f46e5;
    text-decoration: underline;
  }
  & img {
    max-width: 100%;
    border-radius: 8px;
  }
  & ul, & ol {
    padding-left: 24px;
  }
  & li {
    margin-bottom: 4px;
  }
  & h1, & h2, & h3, & h4 {
    margin-top: 24px;
    margin-bottom: 8px;
  }
  & pre {
    max-width: 100%;
    overflow-x: auto;
  }
`;

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 24px;
  border-top: 1px solid #e5e7eb;
  background: white;
`;

const FooterText = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
`;

const FooterLink = styled.a`
  color: #4f46e5;
  text-decoration: underline;
  font-weight: 600;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 12px 32px;
  background: #1C2724;
  color: white;
  border-radius: 24px;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.1s ease;
  &:hover {
    background: #2d3b37;
    transform: translateY(-1px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #1C2724;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
  text-align: center;
  padding: 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1C2724;
  margin: 0;
`;

const ErrorDesc = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
`;

const LoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 12px 32px;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease, transform 0.1s ease;
  &:hover {
    background: #4338ca;
    transform: translateY(-1px);
  }
`;

const SharedFileImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 12px 0;
  display: block;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
`;

const SharedFileLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  margin: 8px 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #4f46e5;
  text-decoration: none;
  font-size: 0.9rem;
  &:hover { background: #f9fafb; }
`;

const StyledKatexContainer = styled.div`
  & .katex { font-size: 1.2rem; }
  & .katex-centered { text-align: center; margin: 15px; }
  .katex-centered .katex { font-size: 1.6rem; }
`;
